import React, {
    useEffect,
    useMemo,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";




import {
    dashboardSelector,

    fetchDashboardOverview,

    setDashboardFilters,

    setDashboardFromDate,

    setDashboardToDate,

} from "../../../../../store/dashboard/dashboardSlice";


/*
|--------------------------------------------------------------------------
| DASHBOARD COMPONENTS
|--------------------------------------------------------------------------
*/

import DashboardHeader
    from "./components/DashboardHeader";

import DashboardFilter
    from "./components/DashboardFilter";

import TodaySnapshot
    from "./components/TodaySnapshot";

import SalesOverview
    from "./components/SalesOverview";

import SalesTrendOverview
    from "./components/SalesTrendChart";

import PaymentOverview
    from "./components/PaymentOverview";

import WebsiteOverview
    from "./components/WebsiteOverview";

import TaskOverview
    from "./components/TaskOverview";

import EmployeeOverview
    from "./components/EmployeeOverview";

import BusinessOverview
    from "./components/BusinessOverview";

import AlertCenter
    from "./components/AlertCenter";


import "./DashboardPage.css";


/*
|--------------------------------------------------------------------------
| DATE FORMATTER
|--------------------------------------------------------------------------
*/

const formatDateForInput = (
    date
) => {

    const year =
        date.getFullYear();


    const month =
        String(
            date.getMonth() + 1
        ).padStart(
            2,
            "0"
        );


    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );


    return (
        `${year}-${month}-${day}`
    );
};


/*
|--------------------------------------------------------------------------
| QUICK DATE RANGE
|--------------------------------------------------------------------------
|
| All quick ranges are converted into exact:
|
| fromDate
| toDate
|
| and sent with the actual quick-period code to backend.
|
|--------------------------------------------------------------------------
*/

const getQuickDateRange = (
    type
) => {

    const now =
        new Date();


    let fromDate =
        new Date(
            now
        );


    let toDate =
        new Date(
            now
        );


    /*
    |--------------------------------------------------------------------------
    | TODAY
    |--------------------------------------------------------------------------
    */

    if (
        type ===
        "TODAY"
    ) {

        fromDate =
            new Date(
                now
            );


        toDate =
            new Date(
                now
            );

    }


    /*
    |--------------------------------------------------------------------------
    | YESTERDAY
    |--------------------------------------------------------------------------
    */

    else if (
        type ===
        "YESTERDAY"
    ) {

        fromDate =
            new Date(
                now
            );


        fromDate.setDate(
            fromDate.getDate() -
            1
        );


        toDate =
            new Date(
                fromDate
            );

    }


    /*
    |--------------------------------------------------------------------------
    | THIS WEEK
    |--------------------------------------------------------------------------
    |
    | Monday -> Today
    |
    |--------------------------------------------------------------------------
    */

    else if (
        type ===
        "THIS_WEEK"
    ) {

        const currentDay =
            now.getDay();


        const difference =
            currentDay === 0
                ? -6
                : 1 - currentDay;


        fromDate =
            new Date(
                now
            );


        fromDate.setDate(
            now.getDate() +
            difference
        );


        toDate =
            new Date(
                now
            );

    }


    /*
    |--------------------------------------------------------------------------
    | THIS MONTH
    |--------------------------------------------------------------------------
    */

    else if (
        type ===
        "THIS_MONTH"
    ) {

        fromDate =
            new Date(
                now.getFullYear(),
                now.getMonth(),
                1
            );


        toDate =
            new Date(
                now
            );

    }


    /*
    |--------------------------------------------------------------------------
    | THIS YEAR
    |--------------------------------------------------------------------------
    */

    else if (
        type ===
        "THIS_YEAR"
    ) {

        fromDate =
            new Date(
                now.getFullYear(),
                0,
                1
            );


        toDate =
            new Date(
                now
            );

    }


    return {

        fromDate:
            formatDateForInput(
                fromDate
            ),

        toDate:
            formatDateForInput(
                toDate
            ),

    };
};


/*
|--------------------------------------------------------------------------
| DASHBOARD PAGE
|--------------------------------------------------------------------------
*/

const DashboardPage = () => {

    const dispatch =
        useDispatch();



    const {

        data = null,

        loading = false,

        refreshing = false,

        error = null,

        filters = {
            period:
                "TODAY",

            fromDate:
                "",

            toDate:
                "",
        },

        lastUpdated = null,

    } = useSelector(
        dashboardSelector
    ) || {};


    /*
    |--------------------------------------------------------------------------
    | INITIAL DASHBOARD LOAD
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            dispatch(
                fetchDashboardOverview(
                    filters
                )
            );

            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [
            dispatch,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | QUICK RANGE
    |--------------------------------------------------------------------------
    */

    const handleQuickRange = (
        type
    ) => {

        const range =
            getQuickDateRange(
                type
            );


        const nextFilters = {

            period:
                type,

            fromDate:
                range.fromDate,

            toDate:
                range.toDate,

        };


        /*
        |--------------------------------------------------------------------------
        | Update visible filter
        |--------------------------------------------------------------------------
        */

        dispatch(
            setDashboardFilters(
                nextFilters
            )
        );


        /*
        |--------------------------------------------------------------------------
        | Reload complete dashboard
        |--------------------------------------------------------------------------
        */

        dispatch(
            fetchDashboardOverview(
                nextFilters
            )
        );

    };


    /*
    |--------------------------------------------------------------------------
    | FROM DATE
    |--------------------------------------------------------------------------
    */

    const handleFromDateChange = (
        value
    ) => {

        dispatch(
            setDashboardFromDate(
                value
            )
        );

    };


    /*
    |--------------------------------------------------------------------------
    | TO DATE
    |--------------------------------------------------------------------------
    */

    const handleToDateChange = (
        value
    ) => {

        dispatch(
            setDashboardToDate(
                value
            )
        );

    };


    /*
    |--------------------------------------------------------------------------
    | APPLY CUSTOM DATE RANGE
    |--------------------------------------------------------------------------
    */

    const handleApplyCustom =
        () => {

            if (
                !filters?.fromDate ||
                !filters?.toDate
            ) {
                return;
            }


            const fromDate =
                new Date(
                    `${filters.fromDate}T00:00:00`
                );


            const toDate =
                new Date(
                    `${filters.toDate}T00:00:00`
                );


            /*
            |--------------------------------------------------------------------------
            | Invalid date
            |--------------------------------------------------------------------------
            */

            if (
                Number.isNaN(
                    fromDate.getTime()
                ) ||
                Number.isNaN(
                    toDate.getTime()
                )
            ) {
                return;
            }


            /*
            |--------------------------------------------------------------------------
            | From > To
            |--------------------------------------------------------------------------
            */

            if (
                fromDate.getTime() >
                toDate.getTime()
            ) {
                return;
            }


            const nextFilters = {

                period:
                    "CUSTOM",

                fromDate:
                    filters.fromDate,

                toDate:
                    filters.toDate,

            };


            dispatch(
                setDashboardFilters(
                    nextFilters
                )
            );


            dispatch(
                fetchDashboardOverview(
                    nextFilters
                )
            );

        };


    /*
    |--------------------------------------------------------------------------
    | RESET TO TODAY
    |--------------------------------------------------------------------------
    */

    const handleReset =
        () => {

            const range =
                getQuickDateRange(
                    "TODAY"
                );


            const resetFilters = {

                period:
                    "TODAY",

                fromDate:
                    range.fromDate,

                toDate:
                    range.toDate,

            };


            dispatch(
                setDashboardFilters(
                    resetFilters
                )
            );


            dispatch(
                fetchDashboardOverview(
                    resetFilters
                )
            );

        };


    /*
    |--------------------------------------------------------------------------
    | REFRESH CURRENT RANGE
    |--------------------------------------------------------------------------
    */

    const handleRefresh =
        () => {

            dispatch(
                fetchDashboardOverview(
                    filters
                )
            );

        };


    /*
    |--------------------------------------------------------------------------
    | FIRST LOAD
    |--------------------------------------------------------------------------
    */

    if (
        loading &&
        !data
    ) {

        return (

            <div
                className="dashboard-page"
            >

                <div
                    className="dashboard-loading"
                >

                    <div
                        className="dashboard-loader"
                    />


                    <h3>
                        Preparing Business Dashboard
                    </h3>


                    <p>
                        Loading sales, collections,
                        website orders, employees,
                        tasks and business activity...
                    </p>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | SAFE DATA
    |--------------------------------------------------------------------------
    */

    const priority =
        data?.priority ||
        {};


    const sales =
        data?.sales ||
        {};


    const salesTrend =
        data?.salesTrend ||
        [];


    const website =
        data?.website ||
        {};


    const tasks =
        data?.tasks ||
        {};


    const employees =
        data?.employees ||
        {};


    const customers =
        data?.customers ||
        {};


    const requirements =
        data?.requirements ||
        {};


    const queries =
        data?.queries ||
        {};


    const products =
        data?.products ||
        {};


    const purchases =
        data?.purchases ||
        {};


    const alerts =
        data?.alerts ||
        [];


    /*
    |--------------------------------------------------------------------------
    | SELECTED PERIOD SNAPSHOT
    |--------------------------------------------------------------------------
    |
    | priority.* is intentionally "today only" in the API.
    |
    | When the user selects Yesterday / Week / Month / Year / Custom, the top
    | summary must use the selected-period aggregates already returned in:
    |
    | sales
    | website.users
    | website.orders
    |
    | This prevents the top cards from showing 0 while Sales Performance below
    | correctly shows the filtered values.
    |
    |--------------------------------------------------------------------------
    */

    const appliedFilter =
        data?.filter ||
        filters ||
        {};


    const appliedPeriod =
        String(
            appliedFilter
                ?.period ||
            "TODAY"
        )
            .trim()
            .toUpperCase();


    const useSelectedPeriodSummary =
        appliedPeriod !==
        "TODAY";


    const snapshotPriority =
        !useSelectedPeriodSummary
            ? priority
            : {
                ...priority,

                todaySales:
                    sales
                        ?.totalSales ??
                    0,

                todayInvoices:
                    sales
                        ?.totalInvoices ??
                    0,

                todayCollection:
                    sales
                        ?.paidAmount ??
                    0,

                todayOutstanding:
                    sales
                        ?.outstandingAmount ??
                    0,

                todayWebsiteOrders:
                    website
                        ?.orders
                        ?.totalOrders ??
                    0,

                todayWebsiteOrderAmount:
                    website
                        ?.orders
                        ?.totalOrderAmount ??
                    0,

                todayNewWebsiteUsers:
                    website
                        ?.users
                        ?.newUsers ??
                    0,
            };


    /*
    |--------------------------------------------------------------------------
    | PAGE
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="dashboard-page"
        >

            {/* ============================================================
                1. EXECUTIVE HEADER
            ============================================================ */}

            <DashboardHeader

                lastUpdated={
                    lastUpdated
                }

                filter={
                    data?.filter ||
                    filters
                }

            />


            {/* ============================================================
                2. GLOBAL BUSINESS DATE FILTER
            ============================================================ */}

            <DashboardFilter

                filters={
                    filters
                }

                refreshing={
                    refreshing
                }

                onQuickRange={
                    handleQuickRange
                }

                onFromDateChange={
                    handleFromDateChange
                }

                onToDateChange={
                    handleToDateChange
                }

                onApply={
                    handleApplyCustom
                }

                onReset={
                    handleReset
                }

                onRefresh={
                    handleRefresh
                }

            />


            {/* ============================================================
                API ERROR
            ============================================================ */}

            {
                error && (

                    <div
                        className="dashboard-error"
                    >
                        {error}
                    </div>

                )
            }


            {/* ============================================================
                BACKGROUND REFRESH
            ============================================================ */}

            {
                refreshing && (

                    <div
                        className="dashboard-refresh-progress"
                    />

                )
            }


            {/* ============================================================
                3. SELECTED BUSINESS SNAPSHOT

                TODAY uses priority.today* API values.
                Other filters use selected-period sales / website aggregates.
            ============================================================ */}

            <TodaySnapshot

                priority={
                    snapshotPriority
                }

                period={
                    appliedPeriod
                }

                filter={
                    appliedFilter
                }

            />


            {/* ============================================================
                4. SALES OVERVIEW

                MAIN FOCUS OF DASHBOARD
            ============================================================ */}

            <SalesOverview

                sales={
                    sales
                }

                filter={
                    data?.filter ||
                    filters
                }

            />


            {/* ============================================================
                5. SALES TREND + PAYMENT POSITION
            ============================================================ */}

            <div
                className="dashboard-two-column dashboard-sales-analysis-row"
            >

                <SalesTrendOverview

                    data={
                        salesTrend
                    }

                />


                <PaymentOverview

                    sales={
                        sales
                    }

                />

            </div>


            {/* ============================================================
                6. E-COMMERCE

                Website users + website orders
            ============================================================ */}

            <WebsiteOverview

                website={
                    website
                }

            />


            {/* ============================================================
                7. TASK / EMPLOYEE CONTROL
            ============================================================ */}

            <div
                className="dashboard-two-column dashboard-operations-row"
            >

                <TaskOverview

                    tasks={
                        tasks
                    }

                />


                <EmployeeOverview

                    employees={
                        employees
                    }

                    tasks={
                        tasks
                    }

                />

            </div>


            {/* ============================================================
                8. COMPLETE BUSINESS OPERATIONS
            ============================================================ */}

            <BusinessOverview

                customers={
                    customers
                }

                requirements={
                    requirements
                }

                queries={
                    queries
                }

                products={
                    products
                }

                purchases={
                    purchases
                }

            />


            {/* ============================================================
                9. BUSINESS ACTION CENTER
            ============================================================ */}

            <AlertCenter

                alerts={
                    alerts
                }

            />

        </div>

    );

};


export default DashboardPage;